// components/FilterChipsBar.tsx
// Drop this file into: src/components/Dashboard/components/FilterChipsBar.tsx
//
// Usage in DashboardTemplate — place between <ActionBar> and <HeaderSection>:
//
//   <FilterChipsBar
//     filters={drillDownFilters}
//     onRemoveFilter={onRemoveFilter}   // new prop — see DashboardTemplate patch
//     onClearAll={onClearDrillDown}
//   />

import React from "react";
import { CloseOutlined, FilterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

type Props = {
  filters: Record<string, any>;
  onRemoveFilter: (column: string) => void;
  onClearAll: () => void;
};

const PURPLE = "#4A1C96";
const LIGHT_PURPLE = "#f3eeff";
const BORDER_PURPLE = "#c9a8f5";

const formatFilterLabel = (column: string, filter: any): string => {
  // column: "region", filter: { type: "equals", value: "North" }
  // => "Region = North"
  // column: "sales", filter: { type: "range", value: { min: 100, max: 500 } }
  // => "Sales: 100 – 500"
  // column: "status", filter: { type: "in", value: ["A","B"] }
  // => "Status: A, B"

  const label = column
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  if (!filter) return label;

  const { type, value } = filter;

  if (type === "equals") return `${label} = ${value}`;
  if (type === "contains") return `${label} ~ "${value}"`;
  if (type === "in") {
    const vals = Array.isArray(value) ? value.join(", ") : value;
    return `${label}: ${vals}`;
  }
  if (type === "range") {
    const min = value?.min !== undefined ? value.min : "…";
    const max = value?.max !== undefined ? value.max : "…";
    return `${label}: ${min} – ${max}`;
  }

  return `${label}: ${JSON.stringify(value)}`;
};

const FilterChipsBar: React.FC<Props> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  const { t } = useTranslation("dashboard");
  const entries = Object.entries(filters);

  if (!entries.length) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        padding: "8px 16px",
        background: LIGHT_PURPLE,
        borderBottom: `1px solid ${BORDER_PURPLE}`,
        borderTop: `1px solid ${BORDER_PURPLE}`,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Label */}
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          fontWeight: 600,
          color: PURPLE,
          marginRight: 4,
        }}
      >
        <FilterOutlined style={{ fontSize: 12 }} />
        {t("filterChips.activeFilters")}
      </span>

      {/* Individual chips */}
      {entries.map(([column, filter]) => (
        <span
          key={column}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px 3px 12px",
            background: "#fff",
            border: `1px solid ${BORDER_PURPLE}`,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 500,
            color: PURPLE,
            cursor: "default",
            userSelect: "none",
          }}
        >
          {formatFilterLabel(column, filter)}
          <CloseOutlined
            onClick={() => onRemoveFilter(column)}
            style={{
              fontSize: 10,
              color: PURPLE,
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
          />
        </span>
      ))}

      {/* Clear all — only show when more than 1 filter active */}
      {entries.length > 1 && (
        <span
          onClick={onClearAll}
          style={{
            fontSize: 12,
            color: PURPLE,
            textDecoration: "underline",
            cursor: "pointer",
            marginLeft: 4,
            opacity: 0.8,
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
        >
          {t("filterChips.clearAll")}
        </span>
      )}
    </div>
  );
};

export default React.memo(FilterChipsBar);
